// src/pages/PedidoPage.tsx — todos os ingressos de um pedido, com QR.
// Rota /pedido/:paymentId. Exige sessão do portal (email + código OTP em
// localStorage); filtra os ingressos do payment_id em POST /meus/ingressos.
// Sem sessão, pedido pendente ou inexistente, mostra fallback com volta.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EVENTOS, getEventoAtual } from '../config/event';
import IngressoCard from '../components/IngressoCard';
import './LandingPage.css';
import './IngressoPage.css';

const API_BASE = 'https://ta3m3foyci.execute-api.sa-east-1.amazonaws.com';

interface Ingresso {
  ticket_number: number;
  nome: string;
  documento: string;
  email: string;
  tipo_ingresso: string;
  documento_meia_entrada?: string | null;
  payment_id?: string;
  payment_approved: boolean;
  evento_id?: string;
}

const PedidoPage: React.FC = () => {
  const { paymentId } = useParams<{ paymentId: string }>();
  const [itens, setItens] = useState<Ingresso[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const email = localStorage.getItem('era_meus_email') || '';
    const codigo = localStorage.getItem('era_meus_code') || '';
    if (!email || !codigo) {
      setError('no-session');
      setIsLoading(false);
      return;
    }
    fetch(`${API_BASE}/meus/ingressos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, codigo }),
    })
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || 'Erro ao buscar pedido.');
        const lista: Ingresso[] = Array.isArray(data) ? data : [];
        const doPedido = lista
          .filter((i) => String(i.payment_id || '') === String(paymentId))
          .sort((a, b) => a.ticket_number - b.ticket_number);
        if (doPedido.length === 0) throw new Error('not-found');
        if (!doPedido.every((i) => i.payment_approved)) throw new Error('not-paid');
        setItens(doPedido);
      })
      .catch((err: any) => setError(err?.message || 'Erro ao buscar pedido.'))
      .finally(() => setIsLoading(false));
  }, [paymentId]);

  const eventoId = itens.length ? itens[0].evento_id : undefined;
  const evento = eventoId && EVENTOS[eventoId] ? EVENTOS[eventoId] : getEventoAtual();

  return (
    <div className="era-ingresso">
      <header className="era-ingresso-top">
        <Link to="/meus-ingressos" className="era-ingresso-voltar">‹ Meus ingressos</Link>
        <p className="era-ingresso-marca">ESPAÇO ERA</p>
      </header>

      <main className="era-ingresso-main era-ingresso-lista">
        {isLoading && <p className="era-ingresso-status" role="status">Carregando pedido…</p>}

        {!isLoading && (error === 'no-session' || error === 'not-found') && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Pedido não encontrado</h1>
            <p className="era-ingresso-texto">
              Entre com seu email e o código de acesso para ver seus ingressos.
            </p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && error === 'not-paid' && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Pagamento pendente</h1>
            <p className="era-ingresso-texto">
              Os QRs deste pedido aparecem após a confirmação do pagamento.
            </p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && error && !['no-session', 'not-found', 'not-paid'].includes(error) && (
          <div className="era-ingresso-card">
            <h1 className="era-ingresso-titulo">Erro ao buscar pedido</h1>
            <p className="era-ingresso-texto">{error}</p>
            <Link to="/meus-ingressos" className="era-primary-btn">Ver meus ingressos</Link>
          </div>
        )}

        {!isLoading && itens.length > 0 && (
          <div className="era-ingresso-pedido">
            <h1 className="era-ingresso-pedido-titulo">
              Pedido •••{String(paymentId).slice(-6)} — {itens.length} ingresso(s)
            </h1>
            {itens.map((ingresso) => (
              <IngressoCard key={ingresso.ticket_number} ingresso={ingresso} evento={evento} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PedidoPage;
